<script>
import {defineComponent, computed} from "vue";
import { useRoute, useRouter } from "vue-router";
import BaseDataTable from "@/javascript/views/components/charts/BaseDataTable.vue";
import BaseFormTable from "@/javascript/views/components/charts/BaseFormTable.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import BaseModal from "../BaseModal.vue";
import LoadingSpinner from "../loadingSpinner.vue";
import Popup from "../popup.vue";
import {CampaignService} from "@/javascript/services/campaign.service";
import {FundService} from "@/javascript/services/fund.service";
import Paginator from "../Pagination.vue";
import QRComponent from "../QRComponent.vue";
import {useUserStore} from "../../../stores/user";
import router from "../../../router/router";

export default defineComponent({
  name: 'BaseShow',
  components: {QRComponent, Paginator, Popup, LoadingSpinner, BaseModal, BaseDataTable, BaseFormTable,},
  props: {
    /**
     * PROP: modelStore
     * Store passed that contains the model data.
     *
     * *The following properties are required in the state of the store for the component to work properly.
     * @property {String} loadingRecord - Whether the record is loading.
     * @property {String} modelName - Model getting displayed for titles and such.
     * @property {Object} currentRecord - The current record.
     * @property {Object} errors - Contains the form errors - only needed if editable prop is true.
     *
     * *The following actions are required in the store to work properly.
     * @function getRecord
     *    - On mounted will fetch the record being shown.
     * @function updateRecord - only needed if editable prop is true
     *    - Will update the record on saving an edit.
     * @function validate - only needed if editable prop is true && if validation is required
     *    - Will validate the record on attempting to save.
     *
     * *The following getters are required in the store to work properly.
     * @function getShowTableData
     *    - Returns an object representing the formatted table data.
     *    - Object returned should contain a 'labels' property and a 'rows' property.
     *    - See BaseTable.vue for format of 'labels' and 'rows' properties.
     * @function getAssociationTableData
     *    - Returns an array with objects representing the table data of the associations.
     *    - Each object in array should contain a 'labels' property and a 'rows' property.
     *    - See BaseTable.vue for format of 'labels' and 'rows' properties.
     * @function getEditingTableData - only needed if editable prop is true
     *    - Returns an array of objects representing the form table row.
     *    - See BaseFormTable.vue file for the format of row objects.
     **/
    modelStore: {
      type: Object,
      required: true,
    },
    /**
     * Will allow editing functionality if true.
     */
    editable: {
      type: Boolean,
      default: false
    }
  },
  emits: [],
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const analyticEventStore = useAnalyticEventStore();

    const userStore = useUserStore();
    let id = route.params.org;
    if (userStore.userScopedToSubdomain) {
      id = userStore.currentOrganization.id;
    }

    // Dynamically pick the right banner service based on modelName
    const bannerService = computed(() => {
      const name = props.modelStore.modelName.toLowerCase();
      if (name === 'campaign') {
        return new CampaignService(id);
      } else if (name === 'fund') {
        return new FundService(id);
      }
      return new CampaignService(id);
    });

    return {
      router,
      route,
      analyticEventStore,
      bannerService
    };
  },
  data() {
    return {
      localRecord: {},
      originalRecord: {},       // store a deep copy when entering edit mode
      editing: false,
      pendingBannerRemoval: false,
      showWarningModal: false,
      removingRecord: false,
      popupMessage: "",
      newBannerFile: null       // File to upload on save
    };
  },
  computed: {
    loading() {
      return this.modelStore.loadingRecord;
    },
    loadingAssociation() {
      return this.modelStore.loadingAssociationRecords || this.modelStore.loadingRecord;
    },
    recordTableData() {
      return this.modelStore.getShowTableData;
    },
    associationTableData() {
      return this.modelStore.getAssociationTableData;
    },
    editingTableData() {
      return this.editable ? this.modelStore.getEditingTableData : [];
    },
    errors() {
      return this.modelStore.errors;
    },
    async canEdit() {
      return this.modelStore.permissions?.edit;
    },
    associationMeta() {
      return this.modelStore.associationMeta;
    },
    donationLink() {
      if (this.modelStore.modelName !== 'Fund' && this.modelStore.modelName !== 'Campaign') return;
      if (!this.modelStore.currentRecord?.slug) return "";

      const resolved = this.router.resolve({
        name: "landing",
        params: { org: this.route.params.org }
      });

      const url = new URL(resolved.href, window.location.origin);

      return `${url}/${this.modelStore.currentRecord.slug}`;
    }
  },
  mounted() {
    this.modelStore.getRecord(this.route.params.id).then(() => {
      this.localRecord = { ...this.modelStore.currentRecord };
      this.originalRecord = JSON.parse(JSON.stringify(this.localRecord));
      this.analyticEventStore.logEvent({
        action: `index-navigate-to-show-${this.modelStore.traceableName}`,
        traceable_type: this.modelStore.traceableType,
        traceable_id: this.localRecord.id,
      });
    });
  },
  methods: {
    back(){
      this.analyticEventStore.logEvent({
        "action": `${this.modelStore.traceableName}-show-back`,
        "traceable_type": this.modelStore.traceableType,
        "traceable_id": this.modelStore.currentRecord.id
      });
      this.router.back();
    },
    tableColumns(data) {
      return data.labels?.length > 0 ? data.labels.length + 1 : 2;
    },
    navigateToAssociationShow(id, association) {
      this.analyticEventStore.logEvent({
        "action": `${this.modelStore.traceableName}-show-navigate-to-${this.modelStore.traceableAssociation.toLowerCase()}`,
        "traceable_type": this.modelStore.traceableAssociation,
        "traceable_id": id
      });
      this.router.push({
        name: 'client-dashboard',
        params: {
          id, org: this.route.params.org,
          panel: association
        }
      });
    },
    cancel() {
      this.analyticEventStore.logEvent({
        "action": `${this.modelStore.traceableName}-edit-cancel`,
        "traceable_type": this.modelStore.traceableType,
        "traceable_id": this.modelStore.currentRecord.id
      });
      this.editing = false;
      // Revert localRecord to the original copy made at the start of editing
      this.localRecord = JSON.parse(JSON.stringify(this.originalRecord));
      this.pendingBannerRemoval = false;
      this.newBannerFile = null;
    },
    async save() {
      await this.modelStore.validate(this.localRecord);
      if (this.errors && Object.keys(this.errors).length) {
        return;
      }

      // 1) upload the cropped file if we have one
      if (this.newBannerFile) {
        try {
          await this.bannerService.uploadBannerImage(
            this.localRecord.id,
            this.newBannerFile
          );
          this.newBannerFile = null;
        } catch {
          this.showPopup("Banner upload failed");
          return;
        }
      }

      // 2) remove banner if flagged
      if (this.pendingBannerRemoval) {
        await this.modelStore.removeBannerImage(this.localRecord.id);
        this.pendingBannerRemoval = false;
      }

      // 3) save the rest of the record
      await this.modelStore.updateRecord(this.localRecord);
      this.editing = false;
      this.localRecord = JSON.parse(JSON.stringify(this.modelStore.currentRecord));
      this.showPopup("Saved!");
    },
    startEditing() {
      this.analyticEventStore.logEvent({
        action: `${this.modelStore.traceableName}-edit-start`,
        traceable_type: this.modelStore.traceableType,
        traceable_id: this.modelStore.currentRecord.id,
      });
      this.editing = true;
      this.newBannerFile = null;
      // refresh record, then deep-copy
      this.modelStore.getRecord(this.route.params.id).then(() => {
        this.localRecord = JSON.parse(JSON.stringify(this.modelStore.currentRecord));
        this.originalRecord = JSON.parse(JSON.stringify(this.localRecord));
        this.pendingBannerRemoval = false;
      });
    },
    handleMarkRemoveImage() {
      this.pendingBannerRemoval = true;
      this.localRecord.banner_image = null;
      this.localRecord.banner_image_preview = null;
      this.newBannerFile = null;
    },
    removeMember() {
      const result = this.modelStore.removeMemberFromTeam();
      if (result) {
        this.showPopup("Team Member Removed");
        this.showWarningModal = false;
        this.modelStore.loadData();
        this.editing = false;
        router.back();
      } else {
        this.showPopup("Something went wrong");
      }
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    }
  }
});
</script>

<template>
  <div class="dashboard-model-show">
    <section class="base-container">
      <header class="flex justify-between items-center">
        <h2 class="subtitle-bold">
          {{ modelStore.modelName }} Details
        </h2>
        <div
          v-if="editing && modelStore.modelName === 'Team Member'"
          class="btn-filled btn-warning text-sm"
        >
          <button @click="showWarningModal = true">
            Remove
          </button>
        </div>
        <slot name="button-slot" />
      </header>
      <main>
        <template v-if="editable && editing">
          <div class="overflow-container">
            <base-form-table
              :record="localRecord"
              :rows="editingTableData"
              :loading="loading"
              :errors="errors"
              no-data-message="There was an issue editing the record."
              :loading-cells="2"
              @input="(key, val) => (localRecord[key] = val)"
              @remove-image="handleMarkRemoveImage"
              @cropped-file="({ key, file }) => (newBannerFile = file)"
            />
          </div>
          <div class="action-button-container">
            <button
              class="btn-outline"
              @click="back"
            >
              Back
            </button>
            <button
              class="btn-outline"
              @click="cancel"
            >
              Cancel
            </button>
            <button
              class="btn-filled"
              @click="save"
            >
              Save
            </button>
          </div>
        </template>
        <template v-else>
          <div class="overflow-container">
            <base-data-table
              v-bind="recordTableData"
              :loading="loading"
              no-data-message="There was an issue fetching the record."
              :loading-cells="2"
              hide-header
            />
          </div>
          <div
            v-if="modelStore.modelName === 'Fund' || modelStore.modelName === 'Campaign'"
            ref="donationQrWrapper"
            class="share-qr"
          >
            <p>QR Code</p>
            <div>
              <QRComponent
                :value="donationLink"
                :size="100"
                :model-name="modelStore.traceableName"
              />
            </div>
          </div>
          <div class="action-button-container">
            <button
              class="btn-outline"
              @click="back"
            >
              Back
            </button>
            <button
              v-if="editable && canEdit"
              class="btn-filled"
              @click="startEditing"
            >
              Edit
            </button>
          </div>
        </template>
      </main>
    </section>
    <template v-if="associationTableData">
      <section
        v-for="(data, association) in associationTableData"
        :key="association"
        class="base-container association-container"
      >
        <header>
          <h2 class="body-1-bold">
            {{ association }}
          </h2>
        </header>
        <div class="overflow-container">
          <base-data-table
            v-bind="data"
            :loading="loadingAssociation"
            :no-data-message="`No ${association} found.`"
            :loading-cells="tableColumns(data)"
            clickable-rows
            @row-click="id => navigateToAssociationShow(id, association)"
          />
          <paginator
            v-if="associationMeta"
            class="pagination"
            :page="associationMeta.page"
            :total-pages="associationMeta.pages"
            :records-showing="tableColumns(data)"
            :records-per-page="associationMeta.limit"
            :total-records="associationMeta.count"
            @next="modelStore.nextAssociationPage"
            @prev="modelStore.prevAssociationPage"
            @move-to-page="modelStore.moveToAssociationPage"
          />
        </div>
      </section>
    </template>

    <base-modal
      v-if="showWarningModal"
      size="small"
      :background-dismissible="!removingRecord"
      @close="() => (showWarningModal = false)"
    >
      <div
        v-if="removingRecord"
        class="flex justify-center items-center"
      >
        <loading-spinner size="icon-large" />
      </div>
      <div v-else>
        <p>Are you sure you want to remove this user from your team?</p>
        <div class="flex flex-row justify-between mt-4">
          <button
            class="btn btn-outline w-1/2 flex justify-center"
            @click="showWarningModal = false"
          >
            GO BACK
          </button>
          <button
            class="btn btn-filled btn-warning ml-2 w-1/2 flex justify-center"
            @click="removeMember"
          >
            REMOVE MEMBER
          </button>
        </div>
      </div>
    </base-modal>

    <popup
      ref="popup"
      :message="popupMessage"
      :duration="1400"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
.share-qr {
  padding: 16px 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  p {
    padding: 16px 8px;
  }
}
.dashboard-model-show {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-lg;

  section {
    width: 100%;

    header {
      padding-bottom: $space-sm;
      margin-bottom: $space-sm;
      text-transform: capitalize;
    }
    &.association-container header {
      margin-bottom: $space-lg;
    }
    main {
      .overflow-container {
        overflow-x: auto;
      }
      .action-button-container {
        margin-top: $space-sm;
        display: flex;
        flex-wrap: wrap;
        gap: $space-sm;
      }
    }
  }
}
</style>
